import {useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import CustomHeader from '../../../components/mazraeKit/headers/homeHeader';
import SpinnerLoading from '../../../components/SpinnerLoading';
import convertNumberToPersianUnits from '../../../utilities/helper/unitConvertor/convertNumberToPersianUnit';
import CardToCardPayment from './CardToCardPayment';
import {usePostPaymentTransaction} from './hooks/useCreateCashTransaction';
import {usePaymentPrice} from './hooks/usePaymentPrice';
import InternetPay from './InternetPay';
import {PaymentMethodValues, TransactionTypesChoices} from './utils/helpers';
import {CashPayMethodTypes} from './utils/types';

interface IState {
  transactionType?: TransactionTypesChoices;
  paymentType?: PaymentMethodValues;
}

const CashPayment = () => {
  const navigate = useNavigate();
  const {state} = useLocation();
  // we have 2 kinds of TransactionType.
  // 1- order : which is when user wants to pay for the whole order at once (e.g. usage after complete information form) the url param : url/orderGuid
  // 2- storeOrder : which is when user wants to pay for a specific order(called storeOrder) in an order (e.g.usage in pay pending my-orders page) the url param : url/orderGuid/storeOrderGuid

  const {transactionType, paymentType} = state as IState;
  const {orderGuid, storeOrderGuid} = useParams();

  const [selectedMethod, setSelectedMethod] =
    useState<CashPayMethodTypes | null>(null);

  const {
    price,
    isLoading: isGetPriceLoading,
    isSuccess: isGetPriceSuccess,
  } = usePaymentPrice({
    transactionType,
    paymentType,
    orderGuid,
    storeOrderGuid,
  });

  const {mutate: createInternetPaymentTransaction, isLoading} =
    usePostPaymentTransaction({
      transactionType,
      paymentType,
      orderGuid,
      storeOrderGuid,
      handleAfterSuccess: data => {
        if (!data && typeof data !== 'string') {
          toast.error(
            'خطای ناشناخته‌ای رخ داده است لطفا با پشتیبانی تماس بگیرید',
          );
          return;
        }

        // Completely redirect to the external URL
        window.location.href = data as string;
      },
    });

  const handleActionButton = () => {
    if (selectedMethod === 'internet') {
      createInternetPaymentTransaction();
    } else if (selectedMethod === 'card-to-card') {
      navigate(
        `/cash-payment/card-to-card-pay/${orderGuid}${
          storeOrderGuid ? '/' + storeOrderGuid : ''
        } `,

        {
          state: {
            paymentType: paymentType,
            transactionType: transactionType,
            price: price,
          },
        },
      );
    }
  };

  const TransactionPrice = () => {
    if (state?.restPrice) return convertNumberToPersianUnits(state?.restPrice);
    if (price) return convertNumberToPersianUnits(price);
  };

  useEffect(() => {
    if (isGetPriceSuccess && !Boolean(price)) {
      toast.error('خطای ناشناخته‌ای رخ داده است لطفا با پشتیبانی تماس بگیرید');
      navigate('/', {replace: true});
    }
  }, [isGetPriceSuccess, price]);

  return (
    <div className="flex flex-col w-full bg-white h-dvh">
      <CustomHeader
        preComponent={() => (
          <div
            className="relative flex items-center gap-2"
            onClick={() => navigate(-1)}
          >
            <i className="icon-nameArrow---Right text-[25px] text-gray-700" />
            <span className="font-medium">پرداخت</span>
          </div>
        )}
      />

      {/* Page Content */}
      <div className="flex flex-col w-full h-full gap-3 p-6 overflow-y-auto">
        <span className="text-sm font-medium text-neutral-600">
          شیوه پرداخت
        </span>
        <InternetPay
          selected={selectedMethod === 'internet'}
          onSelect={() => setSelectedMethod('internet')}
        />
        <CardToCardPayment
          selected={selectedMethod === 'card-to-card'}
          onSelect={() => setSelectedMethod('card-to-card')}
        />
        <div className="flex items-start gap-1 text-red-600">
          <i className="pt-1 icon-namereport1" />
          <span className="text-sm leading-6 ">
            از خاموش بودن فیلترشکن (VPN) قبل از پرداخت اطمینان حاصل کنید.
          </span>
        </div>
      </div>

      {/* Page Action Button */}
      <div className=" w-full  flex px-4 bg-white shadow-[0px_-1px_6px_0px_rgba(0,_0,_0,_0.1)]">
        <button
          type="button"
          className="flex items-center justify-center w-full h-12 mb-4 text-white rounded-lg bg-farm-shop-500 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:pointer-events-none disabled:text-white"
          onClick={handleActionButton}
          disabled={isGetPriceLoading || !selectedMethod}
        >
          {isGetPriceLoading ? (
            <SpinnerLoading size={30} />
          ) : (
            `پرداخت ${TransactionPrice()} تومان`
          )}
        </button>
      </div>
    </div>
  );
};

export default CashPayment;
